import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kSearchItemCore } from "../../../Admin/kReusableCoreTS/kSearchCore";
import { AppTaskApi, AppTaskRow } from "./AppTaskApi";

// AppTask - ONE task as an object (owner, 2026-11-02: "There should be an object representing a task ... use the
// searchable kObject as the base"). It holds the task's state; the requests to `/tasks` stay in `AppTaskActions` /
// `AppTaskCall`, and the strip (`AppTaskFrame`) moves the state here optimistically, back on a refusal.
// ⛔ Habitat-free: no DOM global (the Bridge typecheck reaches App files).

/** What a strip item offers to read (the browser's element fits by shape). */
export interface IAppTaskSource {
    getAttribute(Name: string): string | null;
    querySelector(Selector: string): IAppTaskSourcePart | null;
}

export interface IAppTaskSourcePart {
    textContent: string | null;
    getAttribute(Name: string): string | null;
}

/** The fields of an `index.json` task row (`AppRow` fits by shape - no import, so no cycle with `AppIndex`). */
export interface IAppTaskIndexRow {
    Id: string; Label: string; Value: string; Owner: string; State: string; Until: string; Anchor: string;
    Href: string; Source: string;
}

export class AppTask extends kSearchItemCore {
    public static readonly States: string[] = ["open", "snoozed", "done", "dismissed"];
    public Id: string = "";
    /** `open`, `snoozed`, `done` or `dismissed`. */
    public State: string = "";
    /** `YYYY-MM-DD`, only while snoozed. */
    public Until: string = "";
    /** `me`, `them`, `both` or "". */
    public Owner: string = "";
    public Due: string = "";
    /** `ai`, `you`, `reply` or `reconnect`. */
    public Source: string = "";
    public Value: string = "";
    /** The link to the source message (`#m-...` on the page, `#c/<href>/<anchor>` in the app), or "". */
    public Href: string = "";
    public Anchor: string = "";

    public constructor(Title: string = "") {
        super();
        this.Title = nz(Title);
        this.SearchFields = ["Title", "Value", "Owner"];
    }

    /** DOCUMENTED EXCEPTION: reads one field. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public IsOpen = (): boolean => {
        return this.State === "open";
    };

    /** DOCUMENTED EXCEPTION: reads one field. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public IsDecided = (): boolean => {
        return this.State === "done" || this.State === "dismissed";
    };

    /** Takes `State` (a known word only); answers whether anything changed. */
    public SetState = (State: string, Until: string = ""): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (AppTask.States.indexOf(State) < 0) { return false; }
            let NewUntil: string = "";
            if (State === "snoozed") { NewUntil = nz(Until); }
            if (this.State === State && this.Until === NewUntil) { return false; }
            this.State = State;
            this.Until = NewUntil;
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTask.SetState");
            return false;
        }
    };

    public Done = (): boolean => this.SetState("done");
    public Reopen = (): boolean => this.SetState("open");
    public Dismiss = (): boolean => this.SetState("dismissed");
    public Snooze = (Until: string): boolean => this.SetState("snoozed", Until);

    /**
     * The state as the Tasks tab shows it on `Today` (`YYYY-MM-DD`, the browser's day): a snooze whose day has come is open
     * again - the same rule as `AppTaskRules.StateOf`. ERROR-SUPPRESSED-JUSTIFIED: string compares only.
     */
    public StateOn = (Today: string): string => {
        if (this.State === "done" || this.State === "dismissed") { return this.State; }
        if (this.State === "snoozed") {
            if (/^\d{4}-\d{2}-\d{2}$/.test(this.Until) === true && this.Until <= Today) { return "open"; }
            return "snoozed";
        }
        return "open";
    };

    /** Open on `Today` and not a reconnect suggestion - what a chat card's "N to do" counts. */
    public CountsAsToDo = (Today: string): boolean => {
        return this.StateOn(Today) === "open" && this.Source !== "reconnect";
    };

    /** A task row of `index.json` (`AppIndex.Tasks`); `Source` read by `SourceWord`'s rule (old rows: from the id). */
    public static FromIndexRow = (Row: IAppTaskIndexRow): AppTask => {
        const Task: AppTask = new AppTask("");
        if (getCoreUtils().KErrorMode === true) { return Task; }
        try {
            Task.Id = nz(Row.Id);
            Task.Title = nz(Row.Label);
            Task.Value = nz(Row.Value);
            Task.Owner = nz(Row.Owner);
            Task.State = nz(Row.State);
            Task.Until = nz(Row.Until);
            Task.Anchor = nz(Row.Anchor);
            Task.Href = nz(Row.Href);
            Task.Source = nz(Row.Source);
            if (Task.Source.length === 0 && Task.Id.indexOf(AppTaskApi.ReconnectPrefix) === 0) { Task.Source = "reconnect"; }
            Task.BuildSearchableString();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTask.FromIndexRow");
        }
        return Task;
    };

    /** The service's row (`AppTaskApi`). */
    public static FromRow = (Row: AppTaskRow): AppTask => {
        const Task: AppTask = new AppTask("");
        if (getCoreUtils().KErrorMode === true) { return Task; }
        try {
            Task.Id = nz(Row.Id);
            Task.Title = nz(Row.Label);
            Task.Value = nz(Row.Value);
            Task.Owner = nz(Row.Owner);
            Task.Due = nz(Row.Due);
            Task.Source = nz(Row.Source);
            Task.State = nz(Row.State);
            Task.Until = nz(Row.Until);
            Task.Anchor = nz(Row.Anchor);
            Task.BuildSearchableString();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTask.FromRow");
        }
        return Task;
    };

    /** A strip item of the page (`TaskStateHtml.Item`: `li.tk[data-task-id]`). */
    public static FromStripItem = (Item: IAppTaskSource): AppTask => {
        const Task: AppTask = new AppTask("");
        if (getCoreUtils().KErrorMode === true) { return Task; }
        try {
            Task.Id = nz(Item.getAttribute("data-task-id"));
            Task.State = nz(Item.getAttribute("data-state"));
            Task.Until = nz(Item.getAttribute("data-until"));
            Task.Owner = nz(Item.getAttribute("data-owner"));
            Task.Due = nz(Item.getAttribute("data-due"));
            Task.Source = nz(Item.getAttribute("data-source"));
            const Label: IAppTaskSourcePart | null = Item.querySelector(".tl");
            if (Label !== null) { Task.Title = nz(Label.textContent); }
            const Go: IAppTaskSourcePart | null = Item.querySelector(".tgo");
            if (Go !== null) { Task.Href = nz(Go.getAttribute("href")); }
            Task.BuildSearchableString();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTask.FromStripItem");
        }
        return Task;
    };
}
